export interface DevotionalReading {
  id: string;
  date: string;
  weekday?: string;
  liturgical_season?: string;
  liturgical_title?: string;
  saint_name?: string;
  saint_description?: string;
  saint_image_url?: string | null;
  first_reading_ref?: string;
  first_reading_text?: string;
  gospel_ref?: string;
  gospel_text?: string;
  papal_commentary?: string;
  created_at?: string;
}

export interface DevotionalFavorite {
  id: string;
  user_id: string;
  reading_id: string;
  created_at: string;
  devotional_readings?: DevotionalReading;
}

export interface DevotionalJournalEntry {
  id: string;
  user_id: string;
  reading_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  devotional_readings?: DevotionalReading;
}

interface ApiResponse<T = any> {
  message: string;
  data: T;
  status: number;
}

class DevotionalService {
  private baseUrl: string;

  constructor(baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3030') {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = document.cookie.split('sgo_access_token=')[1]?.split(';')[0];
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Readings
  async getReading(date: string): Promise<DevotionalReading> {
    const response = await this.request<DevotionalReading>(`/devotional/readings/${date}`, { method: 'GET' });
    return response.data;
  }

  async searchReadings(query: string): Promise<DevotionalReading[]> {
    const response = await this.request<DevotionalReading[]>(`/devotional/readings?q=${encodeURIComponent(query)}`, { method: 'GET' });
    return response.data;
  }

  // Favorites
  async getFavorites(): Promise<DevotionalFavorite[]> {
    const response = await this.request<DevotionalFavorite[]>('/devotional/favorites', { method: 'GET' });
    return response.data;
  }

  async toggleFavorite(reading_id: string): Promise<{ favorited: boolean }> {
    const response = await this.request<{ favorited: boolean }>('/devotional/favorites', {
      method: 'POST',
      body: JSON.stringify({ reading_id }),
    });
    return response.data;
  }

  // Journal
  async getJournalEntries(): Promise<DevotionalJournalEntry[]> {
    const response = await this.request<DevotionalJournalEntry[]>('/devotional/journal', { method: 'GET' });
    return response.data;
  }

  async getJournalEntry(readingId: string): Promise<DevotionalJournalEntry | null> {
    const response = await this.request<DevotionalJournalEntry | null>(`/devotional/journal/${readingId}`, { method: 'GET' });
    return response.data;
  }

  async saveJournalEntry(reading_id: string, content: string): Promise<DevotionalJournalEntry> {
    const response = await this.request<DevotionalJournalEntry>('/devotional/journal', {
      method: 'POST',
      body: JSON.stringify({ reading_id, content }),
    });
    return response.data;
  }
}

const devotionalService = new DevotionalService();
export default devotionalService;
